import { API } from '@/modules/api'
import type { TSkywardLunchCartLine } from '@/types/cart'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'
import { defineStore } from 'pinia'
import { formatCurrency } from '@/classes/Helpers'
import { useCartStore } from '@/stores/cart'

export const createUniqueId = (studentAccountId: number) =>
  studentAccountId + '::' + OrderItemTypeCode.SkywardLunch

export const useSkywardLunchStore = defineStore('skywardLunch', {
  state: () => ({
    isLoaded: {} as Record<TStudentAccountId, boolean>,
    balances: {} as Record<TStudentAccountId, number>
  }),

  getters: {
    isLoading() {
      return (studentAccountId: TStudentAccountId) => !(this.isLoaded[studentAccountId] ?? false)
    },
    getBalance(state) {
      return (studentAccountId: TStudentAccountId) => state.balances[studentAccountId] ?? 0
    }
  },

  actions: {
    async fetch(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      this.isLoaded[studentAccountId] = false
      const balance = await API.Get.skywardLunchBalance(studentAccountId)
      this.balances[studentAccountId] = balance

      if (-balance > 0.01) {
        console.debug(
          'Auto-adding cart amount of ' +
            formatCurrency(-balance) +
            ' to family ' +
            studentAccountId
        )
        this.addToCart(districtId, studentAccountId, -balance)
      }

      this.validateCart(districtId, studentAccountId)
      this.isLoaded[studentAccountId] = true
    },

    // This function is used to validate the cart lines for the skyward lunch store
    // It checks if the cart lines are valid compared to the data from the API, and if not, it removes them from the cart.
    // This prevents the cart from attempting to get order line estimates or checking out with invalid data.
    validateCart(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      const cartStore = useCartStore()

      for (const line of cartStore.getCartLines(districtId) as TSkywardLunchCartLine[]) {
        if (
          line.orderItemType === OrderItemType.SkywardLunch &&
          line.studentAccountId === studentAccountId
        ) {
          const balance = this.balances[line.studentAccountId]
          if (!balance) {
            console.log('balance not found, removing from cart', line.studentAccountId)
            this.removeFromCart(districtId, line.studentAccountId)
          }
        }
      }
    },

    addToCart(districtId: TDistrictId, studentAccountId: number, amount: number) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(studentAccountId)
      const existingLine = cartStore.getCartLine(districtId, uniqueId)

      if (amount < 0.01) {
        if (existingLine) {
          this.removeFromCart(districtId, studentAccountId)
        }
        return
      }
      if (existingLine) {
        cartStore.updateCartLine(districtId, uniqueId, { amount })
      } else {
        const line: TSkywardLunchCartLine = {
          uniqueId,
          studentAccountId,
          description: `Added Skyward Lunch Balance to Student Account #${studentAccountId}`,
          amount,
          orderItemType: OrderItemType.SkywardLunch
        }
        cartStore.addCartLine(districtId, uniqueId, line)
      }
    },

    removeFromCart(districtId: TDistrictId, studentAccountId: number) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(studentAccountId)
      cartStore.removeCartLine(districtId, uniqueId)
    },

    clearCartForDistrict(districtId: TDistrictId) {
      const cartStore = useCartStore()
      for (const line of cartStore.getCartLines(districtId)) {
        if (line.orderItemType === OrderItemType.SkywardLunch) {
          this.removeFromCart(districtId, (line as TSkywardLunchCartLine).studentAccountId)
        }
      }
    }
  }
})
